import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CakeIcon from '@mui/icons-material/Cake';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HrAbsenceCalendar from './HrAbsenceCalendar';
import SidePanel from '../util/SidePanel';
import ProfileCard from '../modal/ProfileCard';

interface Props {
  users: any[];
  access: any;
  baseUrl?: string;
}

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; color: string; bg: string }> = ({ icon, label, value, color, bg }) => (
  <div className="ts-card" style={{ flex: 1, minWidth: 150, display: 'flex', alignItems: 'center', gap: 'var(--space-l2)', padding: 'var(--space-l2)' }}>
    <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-input)', background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 40px' }}>{icon}</div>
    <div>
      <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1, color: 'var(--text-color)' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{label}</div>
    </div>
  </div>
);

const HrOverview: React.FC<Props> = ({ users, access, baseUrl = '' }) => {
  const [absences, setAbsences] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [profileUser, setProfileUser] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const [absRes, typesRes] = await Promise.allSettled([
        axios.get('hr-absence'),
        axios.get('hr-absence-type'),
      ]);
      if (absRes.status === 'fulfilled') setAbsences(Array.isArray(absRes.value.data) ? absRes.value.data : []);
      if (typesRes.status === 'fulfilled') setTypes(Array.isArray(typesRes.value.data) ? typesRes.value.data : []);
    } catch {
      // глобальный interceptor
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const typeColor = (typeId: number) => types.find(tp => tp.id === typeId)?.color || 'var(--text-color-calendar)';
  const typeName = (typeId: number) => types.find(tp => tp.id === typeId)?.name || '—';
  const user = (id: number) => users.find(x => x.id === id);

  const outToday = absences.filter(a =>
    a.status !== 'cancelled' && a.status !== 'rejected' && a.status !== 'draft' &&
    todayIso >= a.startDate && todayIso <= a.endDate,
  );
  const pendingCount = absences.filter(a => a.status === 'pending').length;

  const upcoming = useMemo(() => {
    const out: { icon: string; label: string; userId: number; date: string; kind: 'bday' | 'doc' }[] = [];
    const now = new Date();
    const in14 = new Date(); in14.setDate(now.getDate() + 14);
    const within = (d?: string) => {
      if (!d) return false;
      const dt = new Date(d);
      const thisYear = new Date(now.getFullYear(), dt.getMonth(), dt.getDate());
      return thisYear >= new Date(now.getFullYear(), now.getMonth(), now.getDate()) && thisYear <= in14;
    };
    users.forEach(u => {
      if (within(u.date_of_birth)) out.push({ icon: '🎂', label: t('HrBirthday'), userId: u.id, date: u.date_of_birth, kind: 'bday' });
      if (within(u.license_expiry_date)) out.push({ icon: '⚠️', label: t('HrLicenseExpiry'), userId: u.id, date: u.license_expiry_date, kind: 'doc' });
      if (within(u.probation_end_date)) out.push({ icon: '📋', label: t('HrProbationEnd'), userId: u.id, date: u.probation_end_date, kind: 'doc' });
    });
    return out.slice(0, 8);
  }, [users]);

  return (
    <>
      <div className="hr-ov">
        {/* Слева — календарь (3/4) */}
        <div className="hr-ov-cal">
          <HrAbsenceCalendar users={users} />
        </div>

        {/* Справа — два блока в столбик (1/4) */}
        <div className="hr-ov-side">
          <div className="ts-card thin-scroll" style={{ padding: 'var(--space-l2)' }}>
            <div className="section-label"><span className="tr-section-dot" style={{ background: 'var(--color-warning)' }} />{t('HrOutToday')}</div>
            {outToday.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', padding: 'var(--space-l3) 0' }}>{t('Empty')}</div>
            ) : outToday.map(a => {
              const u = user(a.userId);
              const color = typeColor(a.typeId);
              return (
                <div key={a.id} onClick={() => setProfileUser(a.userId)} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)', padding: '6px 0', cursor: 'pointer' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, flex: 1, color: 'var(--text-color)' }}>{u ? `${u.first_name} ${u.last_name}` : `#${a.userId}`}</span>
                  <span className="sbadge" style={{ background: `${color}22`, color }}>{typeName(a.typeId)}</span>
                </div>
              );
            })}
          </div>

          {access?.hr_overview_events === true && (
            <div className="ts-card thin-scroll" style={{ padding: 'var(--space-l2)' }}>
              <div className="section-label"><span className="tr-section-dot" style={{ background: 'var(--color-success)' }} />{t('HrUpcomingEvents')}</div>
              {upcoming.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', padding: 'var(--space-l3) 0' }}>{t('Empty')}</div>
              ) : upcoming.map((e, i) => {
                const u = user(e.userId);
                return (
                  <div key={i} onClick={() => setProfileUser(e.userId)} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-l3)', padding: '6px 0', cursor: 'pointer' }}>
                    <span style={{ fontSize: 13, color: 'var(--text-color)' }}>{e.icon} {e.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, flex: 1, textAlign: 'right', color: 'var(--text-color)' }}>{u ? `${u.first_name} ${u.last_name}` : ''}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <SidePanel open={profileUser !== null} onClose={() => setProfileUser(null)} width={760}>
        {profileUser !== null && (() => {
          const pu = users.find((u: any) => u.id === profileUser) || { id: profileUser, first_name: '', last_name: '' };
          return (
            <ProfileCard
              isSelf={false}
              hrMode
              selectedUser={{ id: pu.id, first_name: pu.first_name, last_name: pu.last_name }}
              users={users}
              access={access}
              baseUrl={baseUrl}
              setShow={(v) => { if (!v) setProfileUser(null); }}
              refreshUserHeder={() => {}}
              refreshUsers={() => { load(); }}
            />
          );
        })()}
      </SidePanel>
    </>
  );
};

export default HrOverview;
